import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sliderr from 'react-slick';

const Slider: typeof Sliderr = (Sliderr as unknown as { default: typeof Sliderr }).default;

interface OverflowSwipeProps {
  visibleItems: number;
  visibleItemsMobile: number;
  modifiers?: string;
  children?: React.ReactNode[];
  afterChange?: (index: number) => void;
}

interface ArrowProps {
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const PrevArrow = ({ className, onClick }: ArrowProps) => {
  const { t } = useTranslation(['common']);

  return (
    <div className={`overflow-swipe-offset-btn overflow-swipe-offset-btn-prev ${className}`}>
      <button
        type="button"
        onClick={onClick}
        data-wx-actionbutton=""
        title={t('previous', { ns: 'common' })}
      >
        <i className="icon-chevron-left no-margin" />
      </button>
    </div>
  );
};

const NextArrow = ({ className, onClick }: ArrowProps) => {
  const { t } = useTranslation(['common']);

  return (
    <div className={`overflow-swipe-offset-btn overflow-swipe-offset-btn-next ${className}`}>
      <button
        type="button"
        onClick={onClick}
        data-wx-actionbutton=""
        title={t('next', { ns: 'common' })}
      >
        <i className="icon-chevron-right no-margin" />
      </button>
    </div>
  );
};

const OverflowSwipe = ({
  children,
  visibleItems,
  visibleItemsMobile,
  modifiers,
}: OverflowSwipeProps) => {
  const sliderRef = useRef<any>(null);
  const dotsRef = useRef<any>(null);

  const [slideCount, setSlideCount] = useState<number>();
  const onSetSlideCount = (count: number): void => setSlideCount(count);

  if (!children || !children.length) return null;

  const onReInit = useCallback(() => {
    if (slideCount === undefined) {
      onSetSlideCount(dotsRef?.current?.childElementCount);
    }
  }, [slideCount]);

  const settings = {
    speed: 300,
    infinite: false,
    dots: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    arrows: true,
    appendDots: (dots: React.ReactNode) => <ul ref={dotsRef}>{dots}</ul>,
    slidesToShow: visibleItems,
    responsive: [
      {
        breakpoint: 600,
        settings: {
          slidesToShow: visibleItemsMobile,
          arrows: false,
        },
      },
      {
        breakpoint: 900,
        settings: {
          arrows: false,
        },
      },
    ],
    draggable: false,
    onReInit,
  };

  return (
    <div data-wx-overflowswipe={modifiers || ''}>
      <div className="overflow-swipe-carousel">
        <Slider ref={sliderRef} {...settings}>
          {children}
        </Slider>
      </div>
    </div>
  );
};

export default OverflowSwipe;
